import { servikMap } from '../../surveys/consts/consts';
import ajax from '../../common/services/httpService';
import { localeData } from '../../common/services/localeService'
import utilService from '../../common/services/utilService'

export default { getWorkingAreas, getServiks, getUsers, save, getById, remove, getServiksBySiteId, getDefaultToSelectWorkingAreaServiks, defaultSelectedWorkingAreaServiks, getServiksByRoomsIds, getSiteActiveCoverage, getSiteSupervisorRoomCoverage, bulkUpdate, getBulkNotificationTaskCoverage }

const BASE_URL = 'workingArea'

async function getWorkingAreas(filter) {
    const workingAreas = await ajax.get(`${BASE_URL}`, null, { siteId: filter.site })
    return workingAreas.map(w => {
        return {
            id: w.id,
            area_name: w.area_name,
            rooms_number: w.rooms.length,
            rooms_names: w.rooms.map(r => r.room_name),
            shifts: w.shifts.map(s => s.name),
            // assign_number: w.supervisor.phone,
            sms: w.sms.isActive,
            email: w.email.isActive,
            counter: w.counter.isActive,
            cleandex: w.cleandex.isActive,
            offline_calls: w.offline_calls.isActive,
            notified: w.notified,
            api: w.api.isActive,
            // Filled by site-active-coverage polling on the Working Area page.
            active_supervisors: [],
            active_supervisors_count: 0,
            assigned_supervisors_count: 0,
        }
    })
}

async function getServiksBySiteId(siteId) {
    const serviks = await ajax.get(`${BASE_URL}/serviks`, null, { siteId, language: localeData.language })
    return serviks
}

/**
 * Loads the per-working-area active coverage for the currently selected site
 * for the manager visibility on the Working Area page. Returns a map of
 * working area id -> { activeCount, assignedCount, activeSupervisors[] }.
 * Polling-friendly: throws on error so the caller can decide to keep the old
 * data instead of clearing it.
 * @param {Number | String} siteId
 */
async function getSiteActiveCoverage(siteId) {
    const rows = await ajax.get(`${BASE_URL}/site-active-coverage`, null, { siteId })
    const map = {}
    if (Array.isArray(rows)) {
        rows.forEach((row) => {
            map[row.id] = {
                activeCount: row.activeCount,
                assignedCount: row.assignedCount,
                activeSupervisors: row.activeSupervisors || [],
            }
        })
    }
    return map
}

/**
 * Site-wide supervisor room coverage for the Work Areas page banner.
 * @param {Number | String} siteId
 */
async function getSiteSupervisorRoomCoverage(siteId) {
    return ajax.get(`${BASE_URL}/site-supervisor-coverage`, null, { siteId })
}

/**
 * Apply one bulk action to selected working areas on a site.
 * @param {{
 * siteId: number|string;
 * workingAreaIds: Array<number|string>;
 * action: 'set_counter'|'add_notification_task'|'remove_notification_task';
 * payload?: object;
 * }} params
 */
async function bulkUpdate({ siteId, workingAreaIds, action, payload }) {
    return ajax.put(`${BASE_URL}/bulk`, {
        siteId,
        workingAreaIds,
        action,
        payload,
    })
}

/**
 * Preview how many selected areas already include a notification task.
 * @param {{
 * siteId: number|string;
 * workingAreaIds: Array<number|string>;
 * taskId: number|string;
 * }} params
 */
async function getBulkNotificationTaskCoverage({ siteId, workingAreaIds, taskId }) {
    return ajax.post(`${BASE_URL}/bulk/task-coverage`, {
        siteId,
        workingAreaIds,
        taskId,
    })
}

async function getServiksByRoomsIds(roomsIds) {
    const serviks = await ajax.post(`${BASE_URL}/rooms/serviks`, {
        roomsIds,
        language: localeData.language
    });
    return serviks
}

async function getUsers(siteId) {
    const users = await ajax.get('user', null, { siteId })
    return users.filter(user => user.firstName && user.lastName && user.level === 'supervisor')
}

async function getServiks() {
    return servikMap.reports
}

/**@param {Number | String} id */
async function getById(id) {
    const res = await ajax.get(`${BASE_URL}/${id}`);
    if (res.manual_calls.length > 0) {
        res.manual_calls.map(call => {
            call.hour = utilService.convertUtcHourToLocalHour(call.hour)
        })
    }
    // Ensure edited working areas have the same cleandex shape as new ones.
    if (res.cleandex) {
        const warningLevel = res.cleaning_warning_level ?? res.cleandex.warning_level ?? null;
        res.cleandex.warning_level = warningLevel === null || warningLevel === '' ? null : Number(warningLevel);
    }
    return res;
}


/**@param {Number | String} id */
async function remove(id) {
    const res = await ajax.delete(`${BASE_URL}/${id}`);
    return res;
}

async function save(workingArea, site_id) {
    if (workingArea.manual_calls.length > 0) {
        workingArea.manual_calls.map(call => {
            call.hour = utilService.convertLocalHourToUtcHour(call.hour)
        })
    }
    if (workingArea.id) {
        const workingAreaData = {
            workingArea: {
                id: workingArea.id,
                name: workingArea.area_name,
                shifts: workingArea.shifts,
                serviks: workingArea.active_notification_task,
                push: workingArea.notified,
                supervisors: workingArea.supervisors,
                email: workingArea.emails,
                sms: workingArea.sms,
                counter: workingArea.counter,
                cleandex: workingArea.cleandex,
                offline_calls: workingArea.offline_calls,
                api: workingArea.api,
                is_suggestion: workingArea.is_suggestion,
                site_id,
                manual_calls: workingArea.manual_calls
            },
            rooms: workingArea.rooms,
        }
        const res = await ajax.put(`${BASE_URL}`, workingAreaData)
        return res
    } else {
        const workingAreaData = {
            workingArea: {
                name: workingArea.area_name,
                shifts: workingArea.shifts,
                serviks: workingArea.active_notification_task,
                push: workingArea.notified,
                supervisors: workingArea.supervisors,
                email: workingArea.emails,
                sms: workingArea.sms,
                counter: workingArea.counter,
                cleandex: workingArea.cleandex,
                offline_calls: workingArea.offline_calls,
                api: workingArea.api,
                is_suggestion: workingArea.is_suggestion,
                site_id,
                manual_calls: workingArea.manual_calls
            },
            rooms: workingArea.rooms,
        }
        const res = await ajax.post(`${BASE_URL}`, workingAreaData)
        return res
    }
}

function getDefaultToSelectWorkingAreaServiks() {
    return [
        {
            id: 605,
            name: 'coffee / water machine'
        },
        {
            id: 646,
            name: 'coffee / water machine'
        },
        {
            id: 761,
            name: 'coffee / water machine'
        },
        {
            id: 604,
            name: 'missing dishes'
        },
        {
            id: 645,
            name: 'missing dishes'
        },
        {
            id: 603,
            name: 'no sugar / coffee'
        },
        {
            id: 644,
            name: 'no sugar / coffee'
        },
        {
            id: 602,
            name: 'missing milk'
        },
        {
            id: 643,
            name: 'missing milk'
        },
        {
            id: 550,
            name: 'general cleaning'
        },
        {
            id: 80,
            name: 'general cleaning'
        },
        {
            id: 165,
            name: 'general cleaning'
        },
		{
			id: 7,
			name: 'bad smell'
		},
		{
			id: 8,
			name: 'dirty floor'
		},
		{
			id: 9,
			name: 'dirty lavatory seat'
		},
		{
			id: 15,
			name: 'missing soap'
		},
		{
			id: 16,
			name: 'missing toilet paper'
		},
        {
			id: 54,
			name: 'full garbage can'
		},
		{
			id: 48,
			name: 'Cleaning performed'
		},
        {
            id: 14,
            name: 'missing paper towel'
        }
	]
}

function defaultSelectedWorkingAreaServiks() {
    return [
        {
			id: 216,
			name: 'call cleaner'
		}
    ]
}